import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  ClipboardCheck, Plus, Building2, FolderOpen,
  AlertTriangle, CheckCircle2, ArrowRight, Percent
} from 'lucide-react';

function CompletionRing({ pct, size = 44 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 80 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" className="stroke-muted" strokeWidth="4" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}
        className="transition-all duration-700" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        className="fill-foreground text-[10px] font-mono font-bold">{Math.round(pct)}%</text>
    </svg>
  );
}

export default function CloseoutDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/closeout/projects').then(r => setProjects(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const phaseLabels = {
    setup: 'Setup', matrix_build: 'Matrix Build', ingest: 'Ingest', classify: 'Classify',
    audit: 'Audit', stakeholder_map: 'Stakeholders', chase: 'Chasing',
    quality_review: 'Quality Review', gates: 'Gate Reviews', turnover: 'Turnover',
  };

  const statusColors = {
    draft: 'bg-muted/50 text-muted-foreground border-border',
    active: 'bg-blue-50 dark:bg-blue-950/50 text-blue-400 border-blue-900',
    review: 'bg-amber-50 dark:bg-amber-950/30 text-primary border-primary/30',
    complete: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
    turned_over: 'bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-400 border-cyan-300 dark:border-cyan-900',
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="closeout-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
            Activate
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Construction Closeout SOP Compliance — Track, file, and verify closeout packages
          </p>
        </div>
        <button
          onClick={() => navigate('/closeout/new')}
          data-testid="new-closeout-project-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-heading uppercase tracking-wider rounded-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-card border border-border rounded-sm" data-testid="empty-state">
          <ClipboardCheck className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm mb-4">No closeout projects yet</p>
          <button
            onClick={() => navigate('/closeout/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary border border-primary/30 text-sm font-heading uppercase tracking-wider rounded-sm hover:bg-primary/15 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map(p => (
            <Link
              key={p.id}
              to={`/closeout/${p.id}`}
              data-testid={`closeout-project-${p.id}`}
              className="block bg-card border border-border rounded-sm p-5 hover:border-border transition-colors group"
            >
              <div className="flex items-center gap-5">
                {/* Completion ring */}
                <CompletionRing pct={p.completion_pct || 0} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="font-heading text-base font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {p.project_name}
                    </h2>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${statusColors[p.status] || statusColors.draft}`}>
                      {p.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {p.project_code && <span className="font-mono text-muted-foreground">{p.project_code}</span>}
                    {p.building_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{p.building_name}</span>}
                    {p.gc_company && <span>{p.gc_company}</span>}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Phase</p>
                    <p className="font-mono text-sm text-foreground/80">{phaseLabels[p.phase] || p.phase}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Divisions</p>
                    <p className="font-mono text-sm text-foreground/80">{p.division_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Documents</p>
                    <p className="font-mono text-sm text-foreground/80">{p.document_count || 0}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
